import { useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Plus, Boxes } from 'lucide-react';
import { CHIP_BASE, CHIP_LABEL, CHIP_TONE } from '../../utils';
import InstanceRowCard from './parts/InstanceRowCard.jsx';

const ACTIVE_CHIP = `${CHIP_BASE} ${CHIP_TONE.accent}`;

/**
 * Provider Detail — Instances tab (CT-4181, admin-only). Three states match the
 * prototype: not-connected (locked empty), connected with zero instances
 * (empty-of-name), and connected with instances (About card + card rows).
 * Rows deep-link to Instance Detail (CT-3897). Stubs fire via outlet `notifySoon`.
 *
 * Deviation from CT-4181's written "Component mapping" (which calls for a
 * `DataTable` + `StatusMessage` empty state): the actual prototype renders
 * instances as cards, matching the row style Overview already uses
 * (`CartContainer` + compact two-line row), not a literal table — and its empty/locked
 * states are bespoke illustrated panels, not `StatusMessage`. Prototype fidelity
 * wins over the AC's literal component list here, same call made on CT-4179.
 */
const InstancesTab = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { provider, onConnect, notifySoon } = useOutletContext();

  // Stabilized so InstanceRowCard's memo isn't defeated by a fresh closure per
  // render. Declared before the early returns below — conditionally skipping a
  // hook call once `provider.isConnected` flips trips React's rules of hooks.
  const handleOpenInstance = useCallback((instanceId) => navigate(instanceId), [navigate]);

  if (!provider.isConnected) {
    return (
      <div className="rounded-[10px] border border-dashed border-edge bg-surface px-6 py-12 text-center">
        <h3 className="mb-1.5 text-sm font-semibold text-content">
          {t('integrations.provider.instancesTab.lockedTitle')}
        </h3>
        <p className="mb-3.5 text-xs text-content-secondary">
          {t('integrations.provider.instancesTab.lockedBody')}
        </p>
        <Button variant="classicPrimary" size="sm" onClick={onConnect}>
          {t('integrations.provider.connect', { name: provider.name })}
        </Button>
      </div>
    );
  }

  if (provider.instances.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-xs font-medium text-content-faint">
          {t('integrations.provider.instancesTab.emptyLabel')}
        </p>
        <div className="flex flex-col items-center gap-3 rounded-[10px] border border-edge bg-surface px-6 py-12 text-center">
          <Boxes size={28} strokeWidth={1.6} className="text-accent" aria-hidden="true" />
          <div className="flex flex-col gap-1.5">
            <h3 className="text-base font-semibold text-content">
              {t('integrations.provider.instancesTab.emptyHeading', { name: provider.name })}
            </h3>
            <p className="mx-auto max-w-md text-sm text-content-secondary">
              {t('integrations.provider.instancesTab.emptyBody', { name: provider.name })}
            </p>
          </div>
          <Button variant="classicPrimary" size="sm" onClick={onConnect}>
            {t('integrations.provider.connect', { name: provider.name })}
          </Button>
        </div>
      </div>
    );
  }

  const connected = provider.breakdown?.working_count ?? 0;
  const attention = provider.breakdown?.needs_attention_count ?? 0;
  const activeCount = provider.instances.length;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <Card.Body>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-content">
              {t('integrations.provider.instancesTab.aboutTitle')}
            </h3>
            <span className="shrink-0 text-xs text-content-faint">
              {t('integrations.provider.instancesTab.summary', { connected, attention })}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-content-muted">
            {t('integrations.provider.instancesTab.aboutBody')}
          </p>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-content">
                {t('integrations.provider.instancesTab.sectionTitle')}
              </h3>
              <Badge variant="neutral" size="small" className={ACTIVE_CHIP}>
                <span className={CHIP_LABEL}>
                  {t('integrations.provider.instancesTab.activeCount', { count: activeCount })}
                </span>
              </Badge>
            </div>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={Plus}
              onClick={() => notifySoon?.('addInstance')}
            >
              {t('integrations.provider.instancesTab.addAnother')}
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            {provider.instances.map((inst) => (
              <InstanceRowCard
                key={inst.id}
                t={t}
                instance={inst}
                providerSlug={provider.slug}
                providerName={provider.name}
                onOpen={handleOpenInstance}
              />
            ))}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default InstancesTab;
